/**
 * Everything Your Plan shows and does, from the consumer's live data.
 *
 * Self-funding counts when the consumer has active self-funding access; advisors and wizards always
 * see it. Once access has expired, Your Plan stays open with everything free (insurance, policy
 * statuses, applications, policy reviews) and an insurance-only report, while the self-funding figures
 * and the planning session give way to a renewal offer. Expiry deletes nothing: renewing brings the
 * same accounts back. The plan client (with or without self-funding) feeds the assessment, the policy statuses and
 * the one report model that both the screen and the downloadable report render. The report leaves
 * guided planning out entirely and can be downloaded only when the model is ready and no save to the
 * plan or its funding sources is on its way.
 */
import { useMemo } from 'react';
import { buildYourPlanClient, deriveConsumerPolicies } from '@shared/consumerPlan';
import { useFundingAssessment, useFundingSourcesState } from '@/features/funding-sources';
import { usePdfReportDownload } from '@/features/pdf-report';
import type { ConsumerStepId } from '../lib/steps';
import { buildYourPlanReportModel } from '../lib/your-plan-summary';
import { useConsumerClient } from './use-consumer-client';
import { useConsumerPlan } from './use-consumer-plan';
import { useSelfFundAccess } from './use-self-fund-access';

/** Several screens finish a plan, so Back follows this plan's path rather than browser history. */
function stepBeforeYourPlan(hasAddedQuote: boolean, hasSelfFunding: boolean): ConsumerStepId {
  if (hasAddedQuote) return 'review';
  return hasSelfFunding ? 'assessment-projection' : 'options';
}

export function useYourPlan(clientId: string) {
  const { access, endsAt, isConsumerSession } = useSelfFundAccess();
  const includesSelfFunding = access === 'active' || !isConsumerSession;
  const { client, status, retry } = useConsumerClient(clientId);
  const {
    plan,
    applications,
    isLoading: isPlanLoading,
    isSaving: isPlanSaving,
  } = useConsumerPlan(clientId);
  const { isSaving: isSourceSaving } = useFundingSourcesState();

  const planClient = useMemo(
    () =>
      client ? buildYourPlanClient(client, { includeSelfFunding: includesSelfFunding }) : null,
    [client, includesSelfFunding],
  );
  const assessment = useFundingAssessment({
    client: planClient,
    clientStatus: status,
    retryClient: retry,
    requireSelfFundingAccount: false,
    includeRecordedPolicies: true,
    combinedCoverageRule: 'engine',
  });

  const selectedQuoteKey = plan?.selectedQuoteKey ?? null;
  const continuedWithoutApplyingQuoteKey = plan?.continuedWithoutApplyingQuoteKey ?? null;
  const policies = useMemo(
    () =>
      deriveConsumerPolicies({
        ltcPolicies: planClient?.multipleFundingSources?.ltcPolicy,
        selectedQuoteKey,
        continuedWithoutApplyingQuoteKey,
        applications,
      }),
    [planClient, selectedQuoteKey, continuedWithoutApplyingQuoteKey, applications],
  );
  const model = useMemo(
    () =>
      buildYourPlanReportModel({
        planClient,
        assessment,
        policies,
        applications,
        selectedQuoteKey,
        includesSelfFunding,
      }),
    [planClient, assessment, policies, applications, selectedQuoteKey, includesSelfFunding],
  );

  const readyModel = model.status === 'ready' ? model.value : null;
  const { downloadPdfReport, isDownloading, edjEmailSent, dismissEdjEmailSent } =
    usePdfReportDownload({
      includeGuidedSnapshot: false,
      client: planClient,
      yourPlan: readyModel,
    });

  return {
    model,
    access,
    isAccessExpired: isConsumerSession && access === 'expired',
    selfFundAccessEndedAt: access === 'expired' ? endsAt : null,
    showPlanningSession: access !== 'expired',
    previousStep: stepBeforeYourPlan(
      policies.some(({ status }) => status !== 'existing-policy'),
      !!readyModel?.selfFunding,
    ),
    report: {
      download: downloadPdfReport,
      canDownload: !!readyModel && !isPlanLoading && !isPlanSaving && !isSourceSaving,
      isDownloading,
      emailSent: edjEmailSent,
      dismissEmailSent: dismissEdjEmailSent,
    },
  };
}
